import {
  keyboardState,
  keyboardGetters,
  keyboardMutations,
  keyboardActions,
} from "./proctoring/keyboardProctoring";
import {
  mouseState,
  mouseGetters,
  mouseMutations,
  mouseActions,
} from "./proctoring/mouseProctoring";
import {
  monitorState,
  monitorGetters,
  monitorMutations,
  monitorActions,
} from "./proctoring/monitorProctoring";
import {
  livekitState,
  livekitGetters,
  livekitMutations,
  livekitActions,
  _room,
} from "./proctoring/livekitProctoring";
import {
  cameraState,
  cameraGetters,
  cameraMutations,
  cameraActions,
  _cameraStream,
  _screenStream,
  captureImageFromStream,
} from "./proctoring/cameraProctoring";

import store from "@/store";
import { apiGet, apiPost, apiPatch } from "@/utils/api";
import { loadLivekit } from "@/utils/lazyLibs";
import {
  noteLostViolation,
  resetEpisodeSignals,
  sendContinues,
} from "./proctoring/episodeSignals";

const state = () => ({
  ...keyboardState,
  ...mouseState,
  ...monitorState,
  ...livekitState,
  ...cameraState,

  isInitialized: false,
  initStatus: "idle",

  showViolationAlert: false,
  violationReason: "",
  lastViolationTime: 0,
  violationCooldown: 3000,
  violationsData: [],

  listenersActive: false,
  activeSection: "mcq",
  maxViolationsReached: false,
  adminWarningAcknowledged: false,

  // Kept in the store, not localStorage: it is per exam and must not be editable.
  violationPolicy: null,
  autoTerminateResolved: null,
});

const getters = {
  ...keyboardGetters,
  ...mouseGetters,
  ...monitorGetters,
  ...livekitGetters,
  ...cameraGetters,

  isInitialized: (state) => state.isInitialized,
  initStatus: (state) => state.initStatus,
  isInitializing: (state) =>
    state.initStatus === "idle" || state.initStatus === "loading",
  showViolationAlert: (state) => state.showViolationAlert,
  violationReason: (state) => state.violationReason,
  violationsData: (state) => state.violationsData,
  maxViolationsReached: (state) => state.maxViolationsReached,

  // Unknown types detect: missing a violation cannot be undone.
  detects: (state) => (type) => {
    if (!type) return true;
    const policy =
      state.violationPolicy?.[state.activeSection] ??
      state.violationPolicy?.default ??
      {};
    return policy[type]?.detect !== false;
  },
};

const mutations = {
  ...keyboardMutations,
  ...mouseMutations,
  ...monitorMutations,
  ...livekitMutations,
  ...cameraMutations,

  SET_MAX_VIOLATIONS_REACHED(state, val) {
    state.maxViolationsReached = val;
  },
  SET_INITIALIZED(state, val) {
    state.isInitialized = val;
  },
  SET_INIT_STATUS(state, val) {
    state.initStatus = val;
  },
  SET_VIOLATION_ALERT(state, { show, reason }) {
    state.showViolationAlert = show;
    state.violationReason = reason || "";
  },
  PUSH_VIOLATION(state, violation) {
    state.violationsData.push(violation);
  },
  SET_LAST_VIOLATION_TIME(state, time) {
    state.lastViolationTime = time;
  },
  SET_LISTENERS(state, val) {
    state.listenersActive = val;
  },
  SET_ACTIVE_SECTION(state, val) {
    state.activeSection = val;
  },
  SET_VIOLATION_POLICY(state, { violationPolicy, autoTerminateResolved }) {
    state.violationPolicy = violationPolicy || null;
    state.autoTerminateResolved =
      typeof autoTerminateResolved === "boolean" ? autoTerminateResolved : null;
  },
  RESET(state) {
    state.isInitialized = false;
    state.initStatus = "idle";
    state.showViolationAlert = false;
    state.violationReason = "";
    state.violationsData = [];
    state.lastViolationTime = 0;
    state.listenersActive = false;
    state.activeSection = "mcq";
    state.maxViolationsReached = false;
    state.adminWarningAcknowledged = false;
    state.violationPolicy = null;
    state.autoTerminateResolved = null;

    // Livekit resets
    state.remoteParticipants = [];
    state.isExamStarted = false;
    state.chatMessages = [];
    state.showAdminWarning = false;
    state.kickedByProctor = false;
    state.screenShareTerminated = false;

    // Camera resets
    state.animationId = null;
    state.lastDetectionTime = 0;
    state.hasCamera = false;
    state.hasMic = false;
    state.hasScreen = false;
    state.isTfReady = false;
    state.eyesClosedStart = null;
    state.lookingAwayStart = null;
    state.multipleFaceStart = null;
    state.noFaceStart = null;
    state.objectDetectedStart = null;
    state.detectedObjects = [];
  },
};

const actions = {
  ...keyboardActions,
  ...mouseActions,
  ...monitorActions,
  ...livekitActions,
  ...cameraActions,

  async initAll(
    { state, commit, dispatch },
    { roomName, participantName, role }
  ) {
    if (state.isInitialized && _cameraStream && _cameraStream.active) {
      return { success: true };
    }

    commit("SET_ROOM_INFO", { roomName, participantName, role });
    commit("SET_INIT_STATUS", "loading");
    // Not awaited: every detector runs until it lands.
    dispatch("loadViolationPolicy");

    try {
      await dispatch("initCamera");
      commit("SET_INIT_STATUS", "ready");
      commit("SET_INITIALIZED", true);

      dispatch("startProctoring");
      setTimeout(() => dispatch("initTensorFlow"), 500);
      return { success: true };
    } catch (err) {
      commit("SET_INIT_STATUS", "error");
      throw err;
    }
  },

  async _publishCamera() {
    if (!_room || !_room.localParticipant || !_cameraStream) return;
    // Already loaded: a room only exists once connectRoom has loaded livekit.
    const { Track } = await loadLivekit();

    const videoTrack = _cameraStream.getVideoTracks()[0];
    const audioTrack = _cameraStream.getAudioTracks()[0];

    const existingVideo = Array.from(
      _room.localParticipant.videoTrackPublications.values()
    ).find((p) => p.trackName === "camera");
    const existingAudio = Array.from(
      _room.localParticipant.audioTrackPublications.values()
    ).find((p) => p.trackName === "microphone");

    if (videoTrack && (!existingVideo || !existingVideo.track)) {
      await _room.localParticipant.publishTrack(videoTrack, {
        name: "camera",
        source: Track.Source.Camera,
        videoCodec: "h264",
        videoEncoding: { maxBitrate: 550000, maxFramerate: 7 },
      });
    }

    if (audioTrack && (!existingAudio || !existingAudio.track)) {
      await _room.localParticipant.publishTrack(audioTrack, {
        name: "microphone",
        source: Track.Source.Microphone,
      });
    }
  },

  async _publishScreen() {
    if (!_room || !_room.localParticipant || !_screenStream) return;
    const { Track } = await loadLivekit();

    const screenTrack = _screenStream.getVideoTracks()[0];
    const existingScreen = Array.from(
      _room.localParticipant.videoTrackPublications.values()
    ).find((p) => p.trackName === "screen");

    if (screenTrack && (!existingScreen || !existingScreen.track)) {
      await _room.localParticipant.publishTrack(screenTrack, {
        name: "screen",
        source: Track.Source.ScreenShare,
        videoCodec: "h264",
        videoEncoding: { maxBitrate: 600000, maxFramerate: 7 },
      });
    }
  },

  async loadViolationPolicy({ commit }) {
    const examId = localStorage.getItem("exam_id");
    if (!examId) return;
    try {
      const res = await apiGet(`/exams/student-exams-available/?id=${examId}`);
      const rows = res?.data?.data;
      const exams = Array.isArray(rows) ? rows : Object.values(rows || {});
      const proctoring = exams.find((e) => e.id === examId)?.exam_proctoring;
      if (!proctoring) return;
      commit("SET_VIOLATION_POLICY", {
        violationPolicy: proctoring.violation_policy,
        autoTerminateResolved: proctoring.auto_terminate_resolved,
      });
    } catch (err) {
      console.error("[Proctoring] Failed to load the violation policy", err);
    }
  },

  signalEpisodeContinues({ state }, { type, reason }) {
    return sendContinues({ type, reason, sectionKind: state.activeSection });
  },

  async triggerViolation({ state, getters, commit, dispatch }, payload) {
    let reason = "";
    let isLog = false;
    let meta = null;
    // Only for the policy lookup; the server classifies from `reason`.
    let type = null;

    if (typeof payload === "string") {
      reason = payload;
    } else {
      reason = payload.reason;
      isLog = payload.isLog || false;
      meta = payload.meta || null;
      type = payload.type || null;
    }

    if (!getters.detects(type)) return;

    const exactMapping = {
      "Tab switching shortcut detected": false,
      "Screenshot attempt detected": false,
      "Developer tools access attempt": true,
      "View source attempt detected": true,
      "Page reload attempt detected": true,
      "Copy Attempt detected": false,
      "Paste Attempt detected": false,
      "Cut Attempt detected": false,
      "User switched browser tab": false,
      "User switched application / lost focus": true,
      "Right click attempt detected": true,
      "keydown Attempt detected": true,
      "visibilitychange Attempt detected": true,
      "blur Attempt detected": true,
      "contextmenu Attempt detected": true,
      "copy Attempt detected": false,
      "paste Attempt detected": false,
      "cut Attempt detected": false,
      "Looking away": false,
      "Prohibited object detected: cell phone": false,
      "Prohibited object detected: mobile phone": false,
      "Prohibited object detected: laptop": false,
      "Prohibited object detected: phone": false,
      "Prohibited object detected: tvmonitor": false,
      "Prohibited object detected: tv": false,
      "Prohibited object detected: book": true,
      "Prohibited object detected: monitor": false,
      "Speaking Detected": false,
      "Background noise detected": true,
      "Multiple displays detected": true,
    };

    if (exactMapping[reason] !== undefined) {
      isLog = exactMapping[reason];
    } else if (reason.startsWith("Prohibited object detected:")) {
      isLog = false;
    }

    const isPriorityViolation =
      reason === "Person switch was detected" ||
      reason === "Multiple persons detected" ||
      reason === "Prohibited object detected: cell phone" ||
      reason === "Prohibited object detected: mobile phone" ||
      reason === "Prohibited object detected: phone";

    const now = Date.now();
    if (
      !isPriorityViolation &&
      now - state.lastViolationTime < state.violationCooldown
    )
      return;

    commit("SET_LAST_VIOLATION_TIME", now);

    let imageBlob = null;
    try {
      if (_cameraStream && _cameraStream.active) {
        imageBlob = await captureImageFromStream(_cameraStream);
      }
    } catch (err) {
      console.error("[Proctoring] Failed to capture event image", err);
    }

    const eventType = isLog ? "log" : "violation";

    if (!isLog) {
      commit("PUSH_VIOLATION", { reason, time: new Date().toISOString() });
    }

    await dispatch("logEvent", {
      event_type: eventType,
      log_data: {
        reason,
        timestamp: new Date().toISOString(),
        event_type: eventType,
        section_kind: state.activeSection,
        ...(meta ? { meta } : {}),
      },
      imageBlob,
    });
    window.dispatchEvent(new CustomEvent("fetch-logs"));
  },

  startProctoring({ state, commit, dispatch }) {
    if (state.listenersActive) return;
    dispatch("startKeyboardProctoring");
    dispatch("startMouseProctoring");
    dispatch("startMonitorProctoring");
    commit("SET_LISTENERS", true);
  },

  stopProctoring({ commit, dispatch }) {
    dispatch("stopKeyboardProctoring");
    dispatch("stopMouseProctoring");
    dispatch("stopMonitorProctoring");
    commit("SET_LISTENERS", false);
  },

  dismissViolation({ commit }) {
    commit("SET_VIOLATION_ALERT", { show: false, reason: "" });
  },

  async cleanup({ commit, dispatch }) {
    dispatch("stopDetection");
    dispatch("stopProctoring");
    dispatch("stopNoiseDetection");
    dispatch("disconnectCameraStreams");
    await dispatch("disconnectLivekit");
    dispatch("cleanupExternalDevice");
    dispatch("cleanupPersonSwitch");
    dispatch("cleanupMultiplePerson");
    dispatch("cleanupFaceVisibility");
    dispatch("cleanupLookingAway");
    resetEpisodeSignals();
    commit("RESET");
  },

  async terminateExam({ dispatch, commit }) {
    // createEvent("TERMINATED") is intentionally not called here
    // Termination is disabled — only cleanup if explicitly triggered (e.g. admin kick)
    await dispatch("cleanup");
    commit("SET_IS_EXAM_STARTED", false);
  },

  async createEvent({ dispatch }, status) {
    try {
      const student = store.state.user?.id;
      const exam = localStorage.getItem("exam_id");
      let attemptId = localStorage.getItem("exam_attempt_id");

      if (attemptId === "null" || attemptId === "undefined") {
        attemptId = null;
      }
      attemptId = attemptId || null;

      if (status === "IN_PROGRESS" && exam && !attemptId) {
        try {
          const res = await apiGet(
            `/exams/student-exams-available/?id=${exam}`
          );
          if (res?.data?.status === 200 && res.data.data) {
            const exams = Array.isArray(res.data.data)
              ? res.data.data
              : Object.values(res.data.data);
            const examData = exams.find((e) => e.id === exam);

            if (
              examData &&
              examData.attempts &&
              Array.isArray(examData.attempts)
            ) {
              const inProgress = examData.attempts.find(
                (a) => a.status === "IN_PROGRESS"
              );
              if (inProgress) {
                attemptId = inProgress.id;
                localStorage.setItem("exam_attempt_id", attemptId);
              }
            }
          }
        } catch (err) {
          console.error(
            "Failed to fetch exam attempts in proctoring store",
            err
          );
        }
      }

      const isEndingStatus = status === "COMPLETED" || status === "TERMINATED";

      if (attemptId) {
        await apiPatch(`/exam-delivery/exam-attempt-update/${attemptId}/`, {
          student,
          exam,
          status: status,
        });

        if (isEndingStatus) dispatch("clearExamSession");
        return;
      }

      const res = await apiPost("/exam-delivery/exam-attempt-create/", {
        student,
        exam,
        status: status,
      });

      const newAttemptId = res.data?.data?.id || res.data?.id;
      if (newAttemptId && status === "IN_PROGRESS") {
        localStorage.setItem("exam_attempt_id", newAttemptId);
      }

      if (isEndingStatus) dispatch("clearExamSession");
    } catch (err) {
      console.error("[Proctoring] Log event failed:", err);
      throw err;
    }
  },

  clearExamSession() {
    localStorage.removeItem("exam_attempt_id");
    localStorage.removeItem("exam_attempt_boolean");
    localStorage.removeItem("exam_id");
    window.dispatchEvent(new CustomEvent("exam-completed"));
  },

  async logEvent(
    { state, commit, dispatch },
    { event_type, log_data, imageBlob }
  ) {
    try {
      const student_id = store.state.user?.id;
      const exam_id = localStorage.getItem("exam_id");
      const attempt_id = localStorage.getItem("exam_attempt_id");
      // Also top level: the server does not parse log_data sent as FormData.
      const section_kind = log_data?.section_kind || state.activeSection;

      if (imageBlob) {
        const formData = new FormData();
        formData.append("event_type", event_type);
        formData.append("log_data", JSON.stringify(log_data));
        if (student_id) formData.append("student_id", student_id);
        if (exam_id) formData.append("exam_id", exam_id);
        if (attempt_id) formData.append("attempt_id", attempt_id);
        if (section_kind) formData.append("section_kind", section_kind);
        formData.append("image", imageBlob, "violation.jpg");

        await apiPost("/exam-delivery/attempt-event-create/", formData);
      } else {
        await apiPost("/exam-delivery/attempt-event-create/", {
          event_type,
          log_data,
          student_id: student_id,
          exam_id,
          attempt_id,
          section_kind,
        });
      }
    } catch (err) {
      console.error("[Proctoring] Log event failed:", err);
      const status = err.response?.status;
      const maxAttemptReached =
        status === 400 &&
        err.response.data?.data?.attempt_status === "MAX_ATTEMPT_REACHED";
      if (!maxAttemptReached && status !== 401 && status !== 403) {
        noteLostViolation();
      }
      if (maxAttemptReached) {
        commit("SET_MAX_VIOLATIONS_REACHED", true);
        // Not cleanup(): it ends in RESET, which clears the flag set above
        // and closes the dialog. Teardown happens on acknowledge.
        dispatch("stopDetection");
        dispatch("stopProctoring");
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
